import { useState, useCallback, useEffect } from "react";
import { employeeTransactionService } from "../services/employeeTransactionService";
import { SALARYTYPES } from "../../utils/enums";
import employeeTransactionAccessService from "../services/employeeTransactionAccessService";
import { SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";

export const useEmployeeTransaction = ({ user, userAccess, showGeneralMessage, employeeId = null, apiFilterPayload }) => {
  const [loading, setLoading] = useState(false);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const [data, setData] = useState([]);
  const [selectedEmployeeHistoryData, setSelectedEmployeeHistoryData] = useState([]);
  const [originalData, setOriginalData] = useState([]);

  const [salaryData, setSalaryData] = useState({ daily: [], monthly: [], quotaBased: [] });
  const [options, setOptions] = useState({
    shiftSchedules: [],
    classifications: [],
    employments: [],
    payTypes: [],
    salaryTypes: [],
    employeeTypes: [],
    processorAccess: [],
  });

  // Exhaustive Field Map (Covers every field in your buildPayload and tabs)
  const TRANSACTION_FIELDS_MAP = {
    // Tab 1: Assignment
    effectiveFrom: "Effective Date",
    destinationHeadId: "Destination Head ID",
    destinationVesselId: "Destination Vessel ID",
    fbReference: "F/B Reference",
    remarks: "Remarks",
    isActive: "Is Active Status",
    isOnLeave: "Is On Leave Status",
    isWithTime: "With Time Tag",
    isWithBenefits: "With Benefits Tag",

    // Tab 2: Employee Info
    assignedToEmployeePersonalInformationId: "Assigned To Person ID",
    medicalClearanceId: "Medical Clearance ID",
    tempEmployeeTypeId: "Temp Employee Type ID",
    classificationId: "Temp Classification ID",
    departmentId: "Temp Unit/Department ID",
    tempPositionId: "Temp Title/Position ID",
    tempVesselId: "Temp Vessel ID",
    employmentId: "Temp Employment ID",
    tempEmployeeStatusId: "Temp Employee Status ID",

    // Tab 3: Compensation
    payTypeId: "Pay Type ID",
    contractFrom: "Contract Start Date",
    contractTo: "Contract End Date",
    salaryType: "Salary Type",
    maintenanceSalaryCombinationV2Id: "Salary Rate Category ID",
    rateAmount: "Rate Amount",
    entitledToOvertime: "OT Entitlement",

    // Tab 4: Schedule
    shiftScheduleId: "Shift Schedule ID",
    restDay: "Rest Day(s)",

    // Header/System
    employeeTransactionProcessorAccessId: "Transaction Type ID",
    status: "Workflow Status"
  };

  // 1. Fetch Main Data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // MODIFIED: Pass the filters from apiFilterPayload
      const filters = apiFilterPayload || []; // THis will ensure that if apiFilterPayload is undefined, it defaults to an empty array, preventing potential errors in the service layer.
      const result = await employeeTransactionService.getAll(filters, employeeId);
      setData(result);
      setOriginalData(result);

    } catch (err) {
      console.error("Debug error: ", err);
      showGeneralMessage("Failed to load transactions", false);
    } finally {
      setLoading(false);
    }
  }, [employeeId, showGeneralMessage, apiFilterPayload]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 1.1 Fetch Employee Data
  const fetchEmployeeHistoryData = useCallback(async (id) => {
    setLoading(true);
    try {
      const result = await employeeTransactionService.getEmployeeHistoryData(id);
      console.log({ result });
      setSelectedEmployeeHistoryData(result);
      return result; // ADDED: Return the data here
    } catch (err) {
      console.error("Debug error: ", err);
      showGeneralMessage("Failed to load transactions", false);
      return []; // Return empty array on error
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  const clearEmployeeHistoryData = () => {
    setSelectedEmployeeHistoryData([]);
  }

  // 2. Fetch Dropdowns
  const fetchDropdowns = useCallback(async () => {
    setDropdownsLoading(true);
    try {
      const metadata = await employeeTransactionService.getMetadata();
      const processorRes = await employeeTransactionAccessService.getAllProcessorAccess() ?? [];

      setOptions({
        ...metadata.options,
        processorAccess: processorRes.data.map(p => ({
          value: p.id,
          label: p.code,
          raw: p // Keep this raw object for any further processing if needed
        })),
      });

      setSalaryData(metadata.salaryData);
    } catch (error) {
      console.error("Dropdown fetch error", error);
    } finally {
      setDropdownsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  // 3. Search Filter
  const handleFilter = useCallback((value) => {
    if (!value) return setData(originalData);
    const lower = value.toLowerCase();
    setData(originalData.filter(f =>
      f?.employeeNumber?.toLowerCase().includes(lower) ||
      f?.employeeName?.toLowerCase().includes(lower) ||
      f?.vesselName?.toLowerCase().includes(lower)
    ));
  }, [originalData]);

  // 4. Payload Builder (Standardized for Service)
  const buildPayload = (form, isAdd = false) => ({
    employeeTransactionProcessorAccessId: form.employeeTransactionProcessorAccessId ? parseInt(form.employeeTransactionProcessorAccessId) : null,

    destinationHeadId: form.destinationHeadId ? parseInt(form.destinationHeadId) : null,
    destinationVesselId: form.destinationVesselId ? parseInt(form.destinationVesselId) : null,
    fbReference: form.fbReference || "",
    effectiveFrom: form.effectiveFrom || null,
    transactionCode: form.transactionCode || "",
    remarks: form.remarks || "",
    isActive: form.isActive ?? true,
    isOnLeave: form?.isOnLeave,
    isWithTime: form?.isWithTime,
    isWithBenefits: form?.isWithBenefits,

    assignedEmployeePersonalInformationId: parseInt(form.assignedEmployeePersonalInformationId),
    assignedToEmployeePersonalInformationId: parseInt(form.assignedToEmployeePersonalInformationId),
    medicalClearanceId: form.medicalClearanceId ? parseInt(form.medicalClearanceId) : null,
    tempEmployeeTypeId: form.tempEmployeeTypeId ? parseInt(form.tempEmployeeTypeId) : null,
    classificationId: form.classificationId ? parseInt(form.classificationId) : null,
    departmentId: form.departmentId ? parseInt(form.departmentId) : null,
    tempPositionId: form.tempPositionId ? parseInt(form.tempPositionId) : null,
    tempVesselId: form.tempVesselId ? parseInt(form.tempVesselId) : null,
    tempEmployeeStatusId: form.tempEmployeeStatusId ? parseInt(form.tempEmployeeStatusId) : null,
    employmentId: form.employmentId ? parseInt(form.employmentId) : null,
    salaryType: form.salaryType || "",
    maintenanceSalaryCombinationV2Id: form.salaryType === SALARYTYPES.MAINTENANCESALARYCOMBINATION ? parseInt(form.maintenanceSalaryCombinationV2Id) : null,
    rateAmount: form.rateAmount ? parseFloat(form.rateAmount) : null,
    entitledToOvertime: form.entitledToOvertime ?? false,

    // Tab 4
    shiftScheduleId: form.shiftScheduleId ? parseInt(form.shiftScheduleId) : null,
    restDay: form.restDay || "",

    // Tab 5
    employeeStatusMaintenanceId: form.employeeStatusMaintenanceId ? parseInt(form.employeeStatusMaintenanceId) : null,

    payTypeId: form.payTypeId ? parseInt(form.payTypeId) : null,
    contractFrom: form.contractFrom || null,
    contractTo: form.contractTo || null,
    status: form.status || "pending",
    ...(isAdd && { createdById: parseInt(user?.id) }),
    updatedById: parseInt(user?.id),
  });

  // 5. Actions
  const saveAudit = async (action, oldData, newData) => {
    const auditEntries = [];
    // Identify the employee name for the 'Field' description
    const empName = newData?.employeeName || oldData?.employeeName || "Transaction";

    Object.keys(TRANSACTION_FIELDS_MAP).forEach(key => {
      const oldVal = oldData?.[key] ?? "N/A";
      const newVal = newData?.[key] ?? "N/A";

      // Only record if the value actually changed
      if (oldVal.toString() !== newVal.toString()) {
        // Format Booleans for readability
        const formatValue = (val) => {
          if (val === true || val === "true") return "Yes";
          if (val === false || val === "false") return "No";
          if (val === null || val === undefined || val === "") return "None";
          return val.toString();
        };

        auditEntries.push({
          setupArea: "Employee Transaction",
          actionTaken: action,
          field: `${empName} - ${TRANSACTION_FIELDS_MAP[key]}`,
          oldValue: formatValue(oldVal),
          newValue: formatValue(newVal),
          dateSaved: new Date(),
          savedById: user?.id
        });
      }
    });

    if (auditEntries.length > 0) {
      await SetAuditTrailBulk({ data: auditEntries });
    }
  };

  const handleAdd = async (form, onSuccess) => {
    // console.log('Final bulkData:', JSON.stringify(payload, null, 2));
    setRequestLoading(true);
    try {
      const payload = buildPayload(form, true);
      await employeeTransactionService.create(payload);
      await saveAudit("Create", {}, { ...payload, employeeName: form.employeeData?.fullName });

      await fetchData();
      showGeneralMessage("Transaction created successfully!", true);
      onSuccess?.();
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "Error creating transaction", false);
      console.error("Debug error", err);
      console.error("Debug error", err?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleUpdate = async (form, recordId, onSuccess) => {
    setRequestLoading(true);
    try {
      const payload = buildPayload(form, false);
      const originalRecord = originalData.find(r => r.id === recordId);
      await employeeTransactionService.update(recordId, payload);
      await saveAudit("Update", originalRecord, payload);

      await fetchData();
      showGeneralMessage("Transaction updated successfully!", true);
      onSuccess?.();
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "Error updating transaction", false);
      console.error("Debug error", err);
      console.error("Debug error", err?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleSubmitForBypassApproval = async (employeeTransactionId) => {
    const userId = user?.id ?? null;
    setRequestLoading(true);
    try {
      const payload = { userId: userId }
      const originalRecord = originalData.find(r => r.id === employeeTransactionId);
      await employeeTransactionService.submitForBypassApproval(employeeTransactionId, payload);
      await saveAudit("Finalize", { status: originalRecord?.status }, { status: "Finalized (Bypassed)", employeeName: originalRecord?.employeeName });

      await fetchData();
      showGeneralMessage("Transaction submitted!", true);
    } catch (error) {
      showGeneralMessage(error?.response?.data || "Error submitting transaction", false);
      console.error("Debug Error:", error);
      console.error("Debug error", error?.message);
    } finally {
      setRequestLoading(false);
    }
  }

  const handleSubmitForApproval = async (employeeTransactionId) => {
    setRequestLoading(true);
    try {
      const originalRecord = originalData.find(r => r.id === employeeTransactionId);
      await employeeTransactionService.markAsForApproval(employeeTransactionId);
      await saveAudit("Approval Request", { status: "Draft" }, { status: "Pending", employeeName: originalRecord?.employeeName });

      await fetchData();
      showGeneralMessage("Transaction submitted for approval!", true);
    } catch (error) {
      showGeneralMessage(error?.response?.data || "Error submitting transaction for approval", false);
      console.log("Submit for Approval Error:", error.response);
      console.error("Debug error", error?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleMarkAsDeparted = async (employeeTransactionId) => {
    setRequestLoading(true);
    try {
      const originalRecord = originalData.find(r => r.id === employeeTransactionId);
      await employeeTransactionService.markAsDeparted(employeeTransactionId);
      await saveAudit("Status Change", { status: "Approved" }, { status: "Departed", employeeName: originalRecord?.employeeName });

      await fetchData();
      showGeneralMessage("Employee has been marked as departed!", true);
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "Error marking employee as departed", false);
      console.error("Debug error", err?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleMarkAsArrived = async (employeeTransactionId, arrivedAt) => {
    setRequestLoading(true);
    try {

      const payload = {
        arrivedAt: arrivedAt || null
      };

      const originalRecord = originalData.find(r => r.id === employeeTransactionId);
      await employeeTransactionService.markAsArrived(employeeTransactionId, payload);
      await saveAudit("Status Change", { status: "Departed" }, { status: "Arrived", employeeName: originalRecord?.employeeName });

      await fetchData();
      showGeneralMessage("Employee has arrived, transaction is complete!", true);
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "Error marking employee as complete", false);
      console.error("Debug error", err?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  return {
    loading,
    dropdownsLoading,
    requestLoading,
    data,
    selectedEmployeeHistoryData,
    options,
    salaryData,
    fetchData,
    fetchEmployeeHistoryData,
    handleFilter,
    handleAdd,
    handleUpdate,
    handleMarkAsDeparted,
    handleSubmitForBypassApproval,
    handleSubmitForApproval,
    handleMarkAsArrived,
    clearEmployeeHistoryData,
  };
};